import React from 'react';
import { ButtonProps, ButtonVariant, ButtonSize } from '../../types/ui';
import { useResponsive } from '../../hooks';
import { spacing, getResponsiveSpacing } from '../../utils/designTokens';

const Button: React.FC<ButtonProps> = ({ 
  children, 
  onClick, 
  type = 'button', 
  variant = ButtonVariant.PRIMARY, 
  size = ButtonSize.MEDIUM, 
  disabled = false,
  fullWidth = false,
  className = '',
  isLoading = false,
  ...rest 
}) => {
  const { isSmallerThan } = useResponsive();
  const isMobile = isSmallerThan('md');
  
  // Get variant-specific styling
  const getVariantClasses = () => {
    switch (variant) {
      case ButtonVariant.PRIMARY:
        return 'bg-primary-charcoal text-accent-white hover:bg-primary-charcoal/90 focus:ring-primary-charcoal/30';
      case ButtonVariant.SECONDARY:
        return 'bg-primary-silver text-primary-charcoal hover:bg-primary-silver/90 focus:ring-primary-silver/30';
      case ButtonVariant.OUTLINE:
        return 'bg-transparent border border-primary-charcoal text-primary-charcoal hover:bg-primary-charcoal/10 focus:ring-primary-charcoal/20';
      case ButtonVariant.DANGER:
        return 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500/30';
      default:
        return 'bg-primary-charcoal text-accent-white hover:bg-primary-charcoal/90 focus:ring-primary-charcoal/30';
    }
  };
  
  // Get size-specific styling - now uses our standardized spacing system
  const getSizeClasses = () => {
    // For mobile, use slightly larger touch targets
    if (isMobile) {
      switch (size) {
        case ButtonSize.SMALL:
          return 'px-2 py-2.5 text-sm';
        case ButtonSize.LARGE:
          return 'px-4 py-3.5 text-lg';
        case ButtonSize.MEDIUM:
        default:
          return 'px-2.5 py-2.5 text-base'; // Reduced horizontal padding for mobile
      }
    } else {
      // Desktop sizes
      switch (size) {
        case ButtonSize.SMALL:
          return 'px-2 py-1.5 text-xs';
        case ButtonSize.LARGE:
          return 'px-4 py-2.5 text-base';
        case ButtonSize.MEDIUM:
        default:
          return spacing.desktop.element + ' text-sm'; // Use our standardized desktop element spacing
      }
    }
  };
  
  // Apply height based on device and size for consistent touch targets
  const getHeightClass = () => {
    if (isMobile) {
      return size === ButtonSize.SMALL ? 'h-10' : spacing.mobile.formButton;
    } else {
      return size === ButtonSize.LARGE ? 'h-11' : spacing.desktop.formButton;
    }
  };

  return (
    <button
      type={type}
      onClick={(e) => {
        // Only prevent default and stop propagation if we have an onClick handler
        // This allows the button to work correctly when wrapped in a Link
        if (onClick) {
          // Ensure event doesn't propagate
          e.stopPropagation();
          if (e && e.nativeEvent) {
            e.nativeEvent.stopImmediatePropagation();
          }

          // Prevent default behavior only when we have a custom onClick
          e.preventDefault();
          
          // Call the onClick handler
          onClick(e);
        }
      }}
      disabled={disabled || isLoading}
      className={`
        ${getVariantClasses()}
        ${getSizeClasses()}
        ${getHeightClass()}
        ${fullWidth ? 'w-full' : ''}
        ${isLoading ? 'relative !text-transparent' : ''}
        rounded-md font-medium transition-colors duration-200
        focus:outline-none focus:ring-2 focus:ring-offset-2
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}
      `}
      {...rest}
    >
      <span className="truncate block">{children}</span>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <svg className="animate-spin h-5 w-5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      )}
    </button>
  );
};

export default Button;